import React from 'react';
import { useTranslation } from 'react-i18next';
import { ContractVariationTag } from './ContractVariationTag';
import type { ContractBadge } from '../utils/contractBadges';

export type ContractSortField =
  | 'contractNumber'
  | 'description'
  | 'contractAmount'
  | 'paidAmount'
  | 'remainingAmount';

/**
 * A single contract row, already reduced to display values by the caller.
 * Keeping this data-only lets the company view feed its rich derivations
 * (replacement "prel." values, version chains) and the provider views feed a
 * simpler read-only set — while the markup, badges and responsive behaviour
 * live in one shared place.
 */
export interface ContractListRow {
  id: string;
  contractNumber: number;
  numberLabel?: string;
  typeLabel: string;
  isVariation: boolean;
  description: string;
  replacesLabel?: string | null;
  etappCount: number;
  /** Formatted contract amount without a currency suffix (e.g. "123 450"). */
  amountLabel: string;
  amountAfterRotLabel?: string;
  totalAfterVariationLabel?: string;
  paidLabel: string;
  paidTitle?: string;
  paidPreliminary?: boolean;
  remainingLabel: string;
  remainingTitle?: string;
  remainingPreliminary?: boolean;
  versionBadge?: ContractBadge | null;
  draftBadge?: ContractBadge | null;
  /** Highlight the row as danger (e.g. a rejected draft) when not selected. */
  danger?: boolean;
  /** An unsent draft or ÄTA the viewer may delete; shows "Ta bort" with a bin. */
  deletable?: boolean;
  /** Signed by both parties: shows "Signerat avtal" (the stored signed PDF). */
  signed?: boolean;
  canCreateVariation?: boolean;
  /** An unfinished ÄTA blocks a new one: why, and which ÄTA (ClickUp 869fkk2jh). */
  variationBlock?: { text: string; agreementId: string };
  hasAgreement?: boolean;
  /** The contract an ÄTA changes: the row is listed indented under it. */
  parentId?: string;
  /** The Beställare's view (ClickUp 869fkju30): no "Ta bort" (the
   * contractor's tool), "Visa signerat avtal" for the signed PDF. */
  customerView?: boolean;
  /** The contract waits on the Beställare: "Granska och signera avtal". */
  review?: boolean;
}

/**
 * Each contract followed by its ÄTA, in variation order, wherever sorting put
 * the contract. An ÄTA whose contract is not in the list stays where it is.
 */
export function groupVariationRows(rows: ContractListRow[]): { row: ContractListRow; child: boolean }[] {
  const ids = new Set(rows.map(row => row.id));
  const isChild = (row: ContractListRow) => !!row.parentId && row.parentId !== row.id && ids.has(row.parentId);
  const children = (parentId: string) => rows
    .filter(row => isChild(row) && row.parentId === parentId)
    .sort((left, right) => Number(left.numberLabel ?? 0) - Number(right.numberLabel ?? 0));
  return rows.filter(row => !isChild(row)).flatMap(row => [
    { row, child: false },
    ...children(row.id).map(child => ({ row: child, child: true }))
  ]);
}

interface ContractListProps {
  rows: ContractListRow[];
  selectedId?: string | null;
  onSelect: (id: string) => void;
  sortField: ContractSortField;
  sortDirection: 'asc' | 'desc';
  onSort: (field: ContractSortField) => void;
  /** Optional header action, e.g. the company "Skapa Kontrakt" button. */
  headerAction?: React.ReactNode;
  /** When set, renders an "Åtgärder" column whose buttons select the contract
   * and jump to the matching section below. */
  onRowAction?: (id: string, action: ContractRowAction) => void;
  /** The page's own buttons for the chosen contract (e.g. "Redigera" and the
   * company's signing), last in its actions row (ClickUp 869fmxfb7). */
  selectedActions?: React.ReactNode;
  /** Content shown when there are no rows. */
  emptyState: React.ReactNode;
  title?: string;
  className?: string;
}

export type ContractRowAction = 'milestones' | 'edit' | 'open' | 'delete' | 'signedDocument' | 'createVariation' | 'agreement' | 'review';

const AmountCell: React.FC<{ label: string; title?: string; preliminary?: boolean }> = ({
  label,
  title,
  preliminary
}) =>
  preliminary ? (
    <span title={title}>
      {label} <small className="text-muted">prel.</small>
    </span>
  ) : (
    <>{label}</>
  );

export function ContractList({
  rows,
  selectedId,
  onSelect,
  sortField,
  sortDirection,
  onSort,
  headerAction,
  onRowAction,
  selectedActions,
  emptyState,
  title,
  className = 'mb-4'
}: ContractListProps) {
  const { t } = useTranslation();
  // One radio group per list view (table and cards render side by side).
  const radioGroup = React.useId();

  // Each action has its own colour and icon, tinted like the status pills
  // (ClickUp 869fa5qf8): signed green, ÄTA amber, agreement violet, edit and
  // stages blue, delete red. No "Historik": the page's history at the bottom
  // covers the whole project, not just this contract. The Beställare gets only
  // their own choices: review when it is their turn, the signed agreement and,
  // for an ÄTA, the agreement overview (ClickUp 869fkju30).
  // Without a button to show (e.g. a contract still being edited, ClickUp
  // 869fmxfb7) there is no actions row.
  const hasRowActions = ({ customerView, review, signed, hasAgreement, canCreateVariation, deletable }: ContractListRow) =>
    !!(review || signed || hasAgreement || canCreateVariation || (deletable && !customerView));
  const rowActionButtons = ({ id, deletable, signed, contractNumber, canCreateVariation, variationBlock, hasAgreement, isVariation, typeLabel, customerView, review }: ContractListRow) => (
    <>
      {review && (
        <button type="button" className="btn btn-sm contract-list-action is-review" onClick={() => onRowAction?.(id, 'review')}>
          <i className="feather-file-text" aria-hidden="true"></i>{t('contracts.list.actionReview')}
        </button>
      )}
      {signed && (
        <button
          type="button"
          className="btn btn-sm contract-list-action is-signed"
          onClick={() => onRowAction?.(id, 'signedDocument')}
          aria-label={isVariation ? `${t('contracts.list.actionSignedDocument')}: ${typeLabel}` : t('contracts.list.actionSignedDocumentAria', { number: contractNumber })}
          title={isVariation ? `${t('contracts.list.actionSignedDocument')}: ${typeLabel}` : t('contracts.list.actionSignedDocumentAria', { number: contractNumber })}
        >
          <i className="feather-award" aria-hidden="true"></i>{t(customerView ? 'contracts.list.actionSignedDocumentView' : 'contracts.list.actionSignedDocument')}
        </button>
      )}
      {canCreateVariation && !variationBlock && (
        <button type="button" className="btn btn-sm contract-list-action is-variation" onClick={() => onRowAction?.(id, 'createVariation')}>
          <i className="feather-plus-circle" aria-hidden="true"></i>{t('variationWizard.create')}
        </button>
      )}
      {canCreateVariation && variationBlock && (
        <span className="contract-list-variation-blocked" data-testid="variation-blocked">
          <button type="button" className="btn btn-sm contract-list-action is-variation" disabled>
            <i className="feather-plus-circle" aria-hidden="true"></i>{t('variationWizard.create')}
          </button>
          <span className="contract-list-variation-reason">
            {variationBlock.text}{' '}
            <button type="button" className="btn btn-link btn-sm contract-list-variation-link" onClick={() => onRowAction?.(variationBlock.agreementId, 'agreement')}>
              {t('variationWizard.blocked.open')}
            </button>
          </span>
        </span>
      )}
      {hasAgreement && (
        <button type="button" className="btn btn-sm contract-list-action is-agreement" onClick={() => onRowAction?.(id, 'agreement')}>
          <i className="feather-layers" aria-hidden="true"></i>{t('variationWizard.agreementTitle')}
        </button>
      )}
      {selectedActions}
      {deletable && !customerView && (
        <button
          type="button"
          className="btn btn-sm contract-list-action is-delete"
          onClick={() => onRowAction?.(id, 'delete')}
          aria-label={isVariation ? `${t('contracts.deleteDraft.action')}: ${typeLabel}` : t('contracts.deleteDraft.actionAria', { number: contractNumber })}
        >
          <i className="feather-trash-2" aria-hidden="true"></i>{t('contracts.deleteDraft.action')}
        </button>
      )}
    </>
  );
  // The chosen contract's actions sit in one row under the list instead of a
  // column per row (ClickUp 869fa5qf8).
  const selectedRow = rows.find(row => row.id === selectedId);
  const groupedRows = groupVariationRows(rows);
  const variationTag = (row: ContractListRow) => (
    <ContractVariationTag number={row.numberLabel} />
  );
  const selectedName = selectedRow
    ? selectedRow.isVariation ? selectedRow.typeLabel : t('contracts.list.contractName', { number: selectedRow.contractNumber })
    : '';

  const SortHeader = ({ field, label, className = '' }: { field: ContractSortField; label: string; className?: string }) => (
    <th
      onClick={() => onSort(field)}
      style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
      className={`user-select-none ${className}`}
    >
      {label}
      {sortField === field && (
        <i className={`feather-chevron-${sortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
      )}
    </th>
  );

  return (
    // Own card markup: Card wraps its children in a card-body, which nested a
    // second body inside it (double padding) and kept the actions row inset.
    <div className={`card ${className}`}>
      <div className="card-header d-flex align-items-center justify-content-between">
        <h5 className="card-title mb-0">{title ?? t('contracts.list.title')}</h5>
        {headerAction}
      </div>
      <div className="card-body">
        {rows.length === 0 ? (
          emptyState
        ) : (
          <>
            {rows.some(row => row.isVariation) && (
              <p className="text-explain">{t('contracts.list.financialExplanation')}</p>
            )}
            {/* Desktop table */}
            <div className="table-responsive d-none d-md-block">
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th style={{ width: '1%' }} aria-label={t('contracts.list.selectColumn', 'Välj')}></th>
                    {/* The contract's name first, its type after it (no number column). */}
                    <SortHeader field="description" label={t('contracts.descriptions.contractNameTitle')} className="contract-list-description" />
                    <th>{t('contracts.columns.contractType')}</th>
                    <th>{t('contracts.columns.etappCount', 'Antal etapper')}</th>
                    <SortHeader field="contractAmount" label={t('contracts.list.amountBeforeRot')} />
                    <SortHeader field="paidAmount" label={t('contracts.list.financedAfterRot')} />
                    <SortHeader field="remainingAmount" label={t('contracts.list.remainingAfterRot')} />
                    <th>{t('contracts.draftStatus.label')}</th>
                  </tr>
                </thead>
                <tbody>
                  {groupedRows.map(({ row, child }) => (
                    <tr
                      key={row.id}
                      onClick={() => onSelect(row.id)}
                      onDoubleClick={() => onRowAction?.(row.id, 'open')}
                      data-contract-row={row.id}
                      data-variation-child={child || undefined}
                      className={[
                        selectedId === row.id ? 'table-primary' : row.danger ? 'table-danger' : '',
                        child ? 'contract-list-child' : ''
                      ].filter(Boolean).join(' ')}
                      style={{ cursor: 'pointer' }}
                    >
                      <td onClick={(e) => e.stopPropagation()} style={{ width: '1%' }}>
                        {/* A radio button: one contract is chosen at a time. */}
                        <input
                          type="radio"
                          name={`${radioGroup}-table`}
                          className="form-check-input"
                          checked={selectedId === row.id}
                          onChange={() => onSelect(row.id)}
                          aria-label={row.isVariation ? `${t('contracts.list.selectColumn')}: ${row.typeLabel}` : t('contracts.list.selectContract', { number: row.contractNumber, defaultValue: 'Välj kontrakt #{{number}}' })}
                        />
                      </td>
                      <td className={`contract-list-description ${child ? 'contract-list-child-cell' : ''}`}>
                        <div>{child && variationTag(row)}{row.description}</div>
                        {row.replacesLabel && (
                          <small className="text-muted">{row.replacesLabel}</small>
                        )}
                      </td>
                      <td>{row.typeLabel}</td>
                      <td>{row.etappCount}</td>
                      <td>
                        <div>{row.amountLabel}</div>
                        {row.amountAfterRotLabel && <div className="small text-muted">{t('contracts.list.amountAfterRot', { amount: row.amountAfterRotLabel })}</div>}
                        {row.totalAfterVariationLabel && <div className="small text-muted">{t('contracts.list.totalAfterVariation', { amount: row.totalAfterVariationLabel })}</div>}
                      </td>
                      <td>
                        <AmountCell
                          label={row.paidLabel}
                          title={row.paidTitle}
                          preliminary={row.paidPreliminary}
                        />
                      </td>
                      <td>
                        <AmountCell
                          label={row.remainingLabel}
                          title={row.remainingTitle}
                          preliminary={row.remainingPreliminary}
                        />
                      </td>
                      <td>
                        {row.versionBadge && (
                          <span className={`badge bg-${row.versionBadge.variant} me-1`}>
                            {row.versionBadge.label}
                          </span>
                        )}
                        {row.draftBadge && (
                          <span className={`badge bg-${row.draftBadge.variant}`}>
                            {row.draftBadge.label}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile card list */}
            <div className="d-md-none">
              {groupedRows.map(({ row, child }) => (
                <div
                  key={row.id}
                  data-mobile-contract-row={row.id}
                  data-variation-child={child || undefined}
                  className={`border rounded p-3 mb-2 ${selectedId === row.id ? 'border-primary bg-light' : ''} ${child ? 'contract-list-child-card' : ''}`}
                  onClick={() => onSelect(row.id)}
                  onDoubleClick={() => onRowAction?.(row.id, 'open')}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <span className="fw-bold d-flex align-items-center">
                      <input
                        type="radio"
                        name={`${radioGroup}-cards`}
                        className="form-check-input me-2 mt-0"
                        checked={selectedId === row.id}
                        onChange={() => onSelect(row.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={row.isVariation ? `${t('contracts.list.selectColumn')}: ${row.typeLabel}` : t('contracts.list.selectContract', { number: row.contractNumber, defaultValue: 'Välj kontrakt #{{number}}' })}
                      />
                      {child ? variationTag(row) : `#${row.numberLabel ?? row.contractNumber}`}
                    </span>
                    <span className="text-end">{t('contracts.list.amountBeforeRotValue', { amount: row.amountLabel })}</span>
                  </div>
                  {row.amountAfterRotLabel && <div className="small text-muted text-end">{t('contracts.list.amountAfterRot', { amount: row.amountAfterRotLabel })}</div>}
                  {row.totalAfterVariationLabel && <div className="small text-muted text-end mb-2">{t('contracts.list.totalAfterVariation', { amount: row.totalAfterVariationLabel })}</div>}
                  <div className="text-truncate mb-1">{row.description}</div>
                  {row.replacesLabel && (
                    <div className="small text-muted mb-1">{row.replacesLabel}</div>
                  )}
                  <div className="text-muted small mb-1">
                    <i className="feather-git-branch me-1"></i>
                    {t('contracts.columns.etappCount', 'Antal etapper')}: {row.etappCount}
                  </div>
                  <div className="d-flex flex-wrap gap-1">
                    {row.versionBadge && (
                      <span className={`badge bg-${row.versionBadge.variant}`}>
                        {row.versionBadge.label}
                      </span>
                    )}
                    <span
                      className={`badge bg-${row.isVariation ? 'warning text-dark' : 'light text-dark border'}`}
                    >
                      {row.typeLabel}
                    </span>
                    {row.draftBadge && (
                      <span className={`badge bg-${row.draftBadge.variant}`}>
                        {row.draftBadge.label}
                      </span>
                    )}
                  </div>
                  <dl className="small mt-2 mb-0">
                    <dt>{t('contracts.list.financedAfterRot')}</dt>
                    <dd><AmountCell label={t('contracts.list.amountValue', { amount: row.paidLabel })} title={row.paidTitle} preliminary={row.paidPreliminary} /></dd>
                    <dt>{t('contracts.list.remainingAfterRot')}</dt>
                    <dd><AmountCell label={t('contracts.list.amountValue', { amount: row.remainingLabel })} title={row.remainingTitle} preliminary={row.remainingPreliminary} /></dd>
                  </dl>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
      {onRowAction && selectedRow && (hasRowActions(selectedRow) || !!selectedActions) && (
        <div className="contract-list-actions-bar" data-testid="contract-list-actions" role="group"
          aria-label={t('contracts.list.actionsFor', { name: selectedName })}>
          <div className="contract-list-actions-buttons">{rowActionButtons(selectedRow)}</div>
        </div>
      )}
    </div>
  );
}
